import React, { useState, useEffect, useMemo } from 'react';

const CaseStudies = ({ onOpenConsultation }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const cases = useMemo(
    () => [
      {
        id: 'fintech-lending',
        title: 'PaperLoan FinTech Digital Lending Engine',
        subtitle: 'Sub-second AI Underwriting & Automated e-KYC Disbursal System',
        category: 'FinTech & Lending',
        image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=1200&q=80',
        description: 'Engineered a resilient, high-concurrency digital lending gateway processing micro-loans, real-time credit scoring, and automated video e-KYC for millions of retail borrowers.',
        metrics: [
          { label: 'Total Disbursed', value: '$120M+' },
          { label: 'Underwriting Latency', value: '120ms' },
          { label: 'Automation Rate', value: '99.4%' },
          { label: 'Daily Concurrency', value: '250K RPS' },
        ],
        security: [
          { badge: 'SOC2 Type II', color: '#00D4FF' },
          { badge: 'PCI-DSS Level 1', color: '#10B981' },
          { badge: 'ISO 27001 Certified', color: '#A78BFA' },
          { badge: 'AES-256 E2EE', color: '#F59E0B' },
        ],
        stack: ['React', 'Node.js', 'Python AI', 'PostgreSQL', 'Redis', 'Docker'],
        results: 'Reduced loan approval turnaround from 48 hours to under 2 minutes with zero compliance infractions and 99.4% end-to-end automation.',
        seoKeywords: 'FinTech microservices architecture, automated lending engine, PCI-DSS compliant credit underwriting, e-KYC video verification API',
        challenge: 'Legacy banking systems relied on manual documentation checks, causing a 48-hour approval delay, high dropout rates (64%), and severe database deadlocks during peak flash-disbursal hours.',
        architecture: 'Microservices architecture deployed on Kubernetes with event-driven Kafka message streams. Features distributed Redis caching for instant credit scoring and async video e-KYC processing pipeline.',
        securityDetails: 'Strict zero-trust security perimeter, AES-256 encryption at rest and in transit, HSM tokenized credit card data storage, automated biometric identity verification, and real-time fraud alert telemetry.',
        roiImpact: [
          '340% increase in monthly loan disbursement velocity.',
          '64% reduction in operational credit assessment overhead.',
          'Zero security incidents or data leakage over 3 years of 24/7 continuous operation.',
        ],
        quote: {
          text: "Hungry Top's engineering transformed our core engine. We went from processing thousands to disbursing millions daily with sub-second underwriting precision.",
          author: 'Chief Technology Officer, PaperLoan Financial Services',
        },
      },
      {
        id: 'healthcare-ai',
        title: 'AI HealthCare Diagnostic & Radiology Cloud',
        subtitle: 'Deep Learning Medical Image Analysis & Tele-Radiology Platform',
        category: 'Healthcare & AI',
        image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80',
        description: 'Telemedicine cloud ecosystem integrated with PyTorch computer vision models for automated medical X-ray and MRI diagnostic triage.',
        metrics: [
          { label: 'Scans Processed', value: '500K+' },
          { label: 'Model Precision', value: '99.8%' },
          { label: 'Diagnostic Speed', value: '1.2 Sec' },
          { label: 'Uptime SLA', value: '99.99%' },
        ],
        security: [
          { badge: 'HIPAA Compliant', color: '#10B981' },
          { badge: 'SOC2 Type II', color: '#00D4FF' },
          { badge: 'GDPR Health Data', color: '#A78BFA' },
          { badge: 'Zero-Trust IAM', color: '#EC4899' },
        ],
        stack: ['Next.js', 'PyTorch', 'FastAPI', 'Docker', 'AWS HealthLake', 'Kubernetes'],
        results: 'Empowered remote medical clinics to generate instant diagnostic triage reports with 99.8% precision and full HIPAA audit compliance.',
        seoKeywords: 'HIPAA compliant medical AI portal, PyTorch radiology computer vision, DICOM cloud storage architecture, tele-radiology SaaS platform',
        challenge: 'Remote hospitals faced severe shortages of specialized radiologists, leading to 14-hour delays in emergency radiology image interpretations.',
        architecture: 'Decoupled DICOM viewer client built on Next.js communicating via gRPC streaming with GPU-accelerated PyTorch FastAPI microservices running on AWS EKS.',
        securityDetails: 'Full DICOM payload anonymization prior to cloud upload, HIPAA & GDPR compliant end-to-end encrypted storage, granular role-based access control (RBAC), and automated compliance audit logging.',
        roiImpact: [
          'Reduced emergency triage response time from 14 hours to 70 seconds.',
          'Over 500,000 diagnostic scans processed with zero diagnostic classification regressions.',
          'Expanded specialized healthcare coverage to 120+ underserved rural health centers.',
        ],
        quote: {
          text: "The accuracy and speed of the AI diagnostic portal built by Hungry Top has directly saved lives in urgent emergency room scenarios.",
          author: 'Head of Clinical Informatics, Global Diagnostic Network',
        },
      },
      {
        id: 'restaurant-commerce',
        title: 'HungryTop Restaurant Commerce Suite',
        subtitle: 'Omnichannel Merchant Cloud, POS Switch & Real-time Dispatch System',
        category: 'Food & Commerce',
        image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80',
        description: 'Scalable restaurant commerce suite connecting multi-tenant merchants, consumers, and riders with live GPS tracking and POS hardware integrations.',
        metrics: [
          { label: 'Active Merchants', value: '1,400+' },
          { label: 'Orders Processed', value: '2.4M+' },
          { label: 'Avg Dispatch', value: '14 Mins' },
          { label: 'Peak Order Volume', value: '45K/Hour' },
        ],
        security: [
          { badge: 'PCI-DSS Level 1', color: '#10B981' },
          { badge: 'ISO 27001', color: '#00D4FF' },
          { badge: 'WAF Guard', color: '#F59E0B' },
          { badge: 'Encrypted POS', color: '#A78BFA' },
        ],
        stack: ['Flutter', 'React', 'Node.js', 'Redis', 'WebSockets', 'PostgreSQL'],
        results: 'Streamlined order dispatch lifecycle and legacy POS integrations, boosting merchant digital order sales by 35%.',
        seoKeywords: 'Multi-tenant restaurant POS cloud, real-time rider dispatch algorithm, WebSocket ordering backend, omni-channel food delivery software',
        challenge: 'Fragmented legacy POS systems and manual delivery assignment caused massive kitchen bottlenecking during peak dinner demand hours.',
        architecture: 'Multi-tenant event-driven platform utilizing WebSockets for real-time order status synchronization, Redis geo-spatial indices for rider allocation, and POS adapter gateway API.',
        securityDetails: 'PCI-DSS Level 1 compliant payment gateway integrations, encrypted Bluetooth & LAN POS hardware bindings, DDOS mitigated API routes via Cloudflare enterprise WAF.',
        roiImpact: [
          'Lifted restaurant partner digital revenues by 35% within 90 days of onboarding.',
          'Reduced order prep-to-dispatch transit window down to 14 minutes.',
          'Handled 45,000 simultaneous peak dinner orders with zero system downtime.',
        ],
        quote: {
          text: "Hungry Top's commerce engine enabled our multi-outlet restaurant brand to scale delivery operations seamless across 85 cities.",
          author: 'VP of Operations, Premier Hospitality Group',
        },
      },
      {
        id: 'enterprise-erp',
        title: 'Enterprise Global Supply Chain & ERP',
        subtitle: 'Multi-Warehouse Inventory & Autonomous Logistics Cloud',
        category: 'Enterprise & ERP',
        image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80',
        description: 'Unified multi-warehouse inventory management system engineered for international manufacturing and supply chain logistics.',
        metrics: [
          { label: 'Stock Accuracy', value: '99.9%' },
          { label: 'Cost Savings', value: '40%' },
          { label: 'System Uptime', value: '99.99%' },
          { label: 'Fulfillment Centers', value: '18 Hubs' },
        ],
        security: [
          { badge: 'SOC2 Type II', color: '#00D4FF' },
          { badge: 'ISO 27001', color: '#10B981' },
          { badge: 'Enterprise SSO/SAML', color: '#A78BFA' },
          { badge: 'Audit Trail', color: '#F59E0B' },
        ],
        stack: ['Java Spring', 'React', 'Kubernetes', 'Azure Cloud', 'PostgreSQL'],
        results: 'Eliminated stock discrepancies and reduced warehouse holding costs by 40% across 18 international fulfillment centers.',
        seoKeywords: 'Global ERP supply chain architecture, Java Spring Boot multi-warehouse cloud, automated inventory stock sync, enterprise logistics software',
        challenge: 'Desynchronized inventory data across 18 overseas warehouses resulted in frequent stockouts, double-fulfillment errors, and over $4M in annual lost merchandise.',
        architecture: 'Eventual consistency distributed database architecture managed by Java Spring Boot microservices deployed across Microsoft Azure multi-region clusters.',
        securityDetails: 'Okta/Azure AD enterprise SAML 2.0 Single Sign-On, immutably recorded database audit logs, strict object-level security policies, and disaster recovery replication.',
        roiImpact: [
          'Saved $4.2M in annual lost stock merchandise.',
          'Achieved 99.9% real-time inventory count accuracy across global warehouses.',
          'Reduced cross-border order dispatch clearance processing from 2 days to 3 hours.',
        ],
        quote: {
          text: "The ERP architecture delivered by Hungry Top is the backbone of our global logistics network today.",
          author: 'Chief Information Officer, Global Supply Logistics Inc.',
        },
      },
      {
        id: 'algo-trading',
        title: 'High-Frequency Ultra-Low Latency Algo-Trading Engine',
        subtitle: 'Sub-Millisecond Order Routing & Risk Analytics Engine',
        category: 'High-Frequency Trading',
        image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1200&q=80',
        description: 'Ultra-low latency algorithmic order routing & real-time risk evaluation engine built for institutional capital markets.',
        metrics: [
          { label: 'Order Latency', value: '450μs' },
          { label: 'Daily Trades', value: '15M+' },
          { label: 'Reliability SLA', value: '99.999%' },
          { label: 'Throughput', value: '50K Msg/Sec' },
        ],
        security: [
          { badge: 'FINRA Compliant', color: '#00D4FF' },
          { badge: 'ISO 27001', color: '#10B981' },
          { badge: 'Kernel Bypass SEC', color: '#EC4899' },
          { badge: 'Zero Packet Drop', color: '#F59E0B' },
        ],
        stack: ['Go (Golang)', 'C++', 'Redis', 'Kafka', 'DPDK', 'Linux Kernel Bypass'],
        results: 'Processed over 15M+ daily algorithmic trade executions with sub-millisecond risk checks and zero order dropouts.',
        seoKeywords: 'Low latency algorithmic trading engine, C++ DPDK order routing, sub-millisecond risk check architecture, high frequency capital market software',
        challenge: 'Existing order execution stack suffered from micro-burst packet loss and high latency jitter (over 45ms), missing high-yield arbitrage execution windows.',
        architecture: 'Custom C++ execution core leveraging DPDK network card kernel bypass, coupled with Golang risk evaluation sidecars and lock-free ring-buffer shared memory queues.',
        securityDetails: 'HARDENED Linux kernel security parameters, encrypted point-to-point fiber interconnects, real-time risk margin limits, and immutable compliance trade execution archives.',
        roiImpact: [
          'Reduced end-to-end execution latency from 45ms down to 450 microseconds.',
          'Handled 15M+ daily executions with zero packet drops during high volatility market openings.',
          'Generated $18M+ in additional arbitrage yield within the first year of deployment.',
        ],
        quote: {
          text: "Hungry Top's low-latency C++ systems engineers gave us a distinct speed advantage in ultra-competitive trading venues.",
          author: 'Head of Quantitative Infrastructure, Capital Alpha Partners',
        },
      },
      {
        id: 'ecommerce-ai',
        title: 'AI Neural Search & Vector Recommendation Platform',
        subtitle: 'Real-Time Semantic Vector Search & Personalization Engine',
        category: 'Retail AI',
        image: 'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=1200&q=80',
        description: 'Real-time vector search re-ranking and hyper-personalized product recommendation engine handling millions of active shoppers.',
        metrics: [
          { label: 'Conversion Boost', value: '+42%' },
          { label: 'Catalog Items', value: '2M+' },
          { label: 'Search Latency', value: '45ms' },
          { label: 'AOV Increase', value: '+28%' },
        ],
        security: [
          { badge: 'GDPR Compliant', color: '#10B981' },
          { badge: 'SOC2 Type II', color: '#00D4FF' },
          { badge: 'Anonymized AI', color: '#A78BFA' },
          { badge: 'Cloudflare WAF', color: '#F59E0B' },
        ],
        stack: ['Python AI', 'PyTorch', 'Pinecone Vector DB', 'Next.js', 'Redis', 'Kafka'],
        results: 'Lifted checkout conversion rate by 42% and increased average order value (AOV) by 28% across retail partner brands.',
        seoKeywords: 'E-commerce AI vector search engine, Pinecone recommendation architecture, real-time shopper semantic re-ranking, high conversion retail SaaS',
        challenge: 'Standard keyword search failed to understand natural intent queries (e.g., "breathable summer trail running shoes"), leading to 45% search exit rates.',
        architecture: 'Transformer neural embedding models generating 1536-dimension embeddings stored in Pinecone Vector Database with Redis caching for real-time sub-50ms search query response.',
        securityDetails: 'Anonymized user clickstream tracking, strict GDPR user data privacy compliance, automated data scrubbing pipelines, and zero third-party data tracking sharing.',
        roiImpact: [
          'Boosted e-commerce store checkout conversion rate by +42%.',
          'Increased Average Order Value (AOV) by 28% via contextual upsells.',
          'Sub-50ms query latency across a catalog of over 2 Million products.',
        ],
        quote: {
          text: "The AI neural search recommendation engine completely revitalized our product discovery funnel.",
          author: 'Chief Digital Officer, Omnichannel Retail Brands',
        },
      },
      {
        id: 'ev-fleet-iot',
        title: 'Smart EV Fleet & Telemetry IoT Cloud',
        subtitle: 'High-Throughput Vehicle Telemetry & Predictive Battery AI',
        category: 'IoT & Automotive',
        image: 'https://images.unsplash.com/photo-1558442074-3c19857bc1dc?auto=format&fit=crop&w=1200&q=80',
        description: 'Real-time vehicle telemetry ingestion, predictive battery health analytics, and AI route optimization platform for commercial EV fleets.',
        metrics: [
          { label: 'Connected EVs', value: '50K+' },
          { label: 'Daily Telemetry', value: '1B+ Logs' },
          { label: 'Battery Savings', value: '18%' },
          { label: 'Ingestion Rate', value: '120K/Sec' },
        ],
        security: [
          { badge: 'ISO 27001', color: '#00D4FF' },
          { badge: 'TLS 1.3 mTLS', color: '#10B981' },
          { badge: 'Automotive Cyber', color: '#EC4899' },
          { badge: 'SOC2 Type II', color: '#F59E0B' },
        ],
        stack: ['Rust', 'Node.js', 'MQTT Brokers', 'TimescaleDB', 'Kafka', 'React'],
        results: 'Engineered high-throughput IoT pipeline processing 1 Billion telemetry records daily with 18% battery lifespan extension.',
        seoKeywords: 'Rust IoT vehicle telemetry cloud, MQTT high-throughput ingestion, predictive EV battery health AI, commercial fleet management software',
        challenge: 'Ingesting continuous sensor data from 50,000 electric vehicles caused database lockouts and telemetry dropped messages during peak transit hours.',
        architecture: 'High-performance Rust MQTT ingest layer feeding Apache Kafka streaming buffers, persisted in TimescaleDB time-series database with ML predictive degradation models.',
        securityDetails: 'Mutual TLS (mTLS) 1.3 client certificate authentication per vehicle hardware unit, hardware security module (HSM) device key management, and zero telemetry tampering verification.',
        roiImpact: [
          'Extended commercial fleet EV battery pack lifespan by 18%.',
          'Ingested 1 Billion daily telemetry data points with zero message dropouts.',
          'Reduced vehicle roadside breakdown incidents by 54% via AI predictive alerts.',
        ],
        quote: {
          text: "Hungry Top designed an IoT backend that easily handles our rapidly growing fleet without breaking a sweat.",
          author: 'VP of Fleet Engineering, MobilityX EV Systems',
        },
      },
      {
        id: 'cyber-soc-ai',
        title: 'Automated Cyber Threat Intelligence SOC Platform',
        subtitle: 'Autonomous LLM AI Agents for Zero-Day Threat Mitigation',
        category: 'Cyber Security',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80',
        description: 'Autonomous Security Operations Center (SOC) platform leveraging LLM AI agents to detect, isolate, and remediate zero-day cyber attacks in real-time.',
        metrics: [
          { label: 'Auto-Remediate', value: '98%' },
          { label: 'MTTR Speed', value: '< 45 Sec' },
          { label: 'False Positives', value: '0.01%' },
          { label: 'Log Analysis', value: '10TB/Day' },
        ],
        security: [
          { badge: 'SOC2 Type II', color: '#00D4FF' },
          { badge: 'ISO 27001', color: '#10B981' },
          { badge: 'FEDRAMP Ready', color: '#A78BFA' },
          { badge: 'Zero Trust SOC', color: '#EC4899' },
        ],
        stack: ['Python AI', 'LangChain', 'Elasticsearch', 'Kubernetes', 'Go', 'React'],
        results: 'Reduced Mean Time to Remediate (MTTR) from hours to under 45 seconds while eliminating false positive security alerts.',
        seoKeywords: 'Autonomous Cyber Security SOC platform, AI LLM threat detection agent, real-time SOC MTTR automation, SOC2 compliant incident response cloud',
        challenge: 'Security teams were overwhelmed by 10,000+ daily alert noise, leading to critical alert fatigue and delayed response to real zero-day exploit attempts.',
        architecture: 'Autonomous multi-agent LangChain AI pipeline analyzing raw firewall/SIEM log streams stored in Elasticsearch, triggering automated Kubernetes network policy isolates.',
        securityDetails: 'Built under strict FedRAMP & SOC2 Ready standards, Air-Gapped deployment option, immutable cryptographically signed incident ledgers, and multi-factor authorization checkpoints.',
        roiImpact: [
          'Reduced Mean Time to Remediate (MTTR) from 4.5 hours down to 45 seconds.',
          'Autonomously remediated 98% of security incidents without human fatigue.',
          'Saved $1.8M in security operations center staffing and triage costs.',
        ],
        quote: {
          text: "Hungry Top's AI SOC platform gives our enterprise bank complete peace of mind against emerging zero-day cyber threats.",
          author: 'Chief Information Security Officer, Sentinel National Bank',
        },
      },
    ],
    []
  );

  const categories = ['All', 'FinTech & Lending', 'Healthcare & AI', 'Food & Commerce', 'Enterprise & ERP', 'High-Frequency Trading', 'Retail AI', 'IoT & Automotive', 'Cyber Security'];

  // Filter cases based on category and search query
  const filteredCases = cases.filter((c) => {
    const matchesCategory = selectedCategory === 'All' || c.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.stack.some((tech) => tech.toLowerCase().includes(searchQuery.toLowerCase())) ||
      c.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Dynamically inject JSON-LD Schema when modal opens for maximum SEO crawlability
  useEffect(() => {
    if (selectedCase) {
      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        headline: selectedCase.title,
        description: selectedCase.description,
        image: selectedCase.image,
        author: {
          '@type': 'Organization',
          name: 'Hungry Top Technologies',
          url: 'https://hungrytop.com',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hungry Top Technologies',
          logo: {
            '@type': 'ImageObject',
            url: 'https://hungrytop.com/logo.svg',
          },
        },
        keywords: selectedCase.seoKeywords,
        articleBody: `${selectedCase.description} Challenge: ${selectedCase.challenge} Architecture: ${selectedCase.architecture} Security: ${selectedCase.securityDetails} Results: ${selectedCase.results}`,
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'case-study-jsonld';
      script.innerHTML = JSON.stringify(schemaData);
      document.head.appendChild(script);

      return () => {
        const existingScript = document.getElementById('case-study-jsonld');
        if (existingScript) {
          existingScript.remove();
        }
      };
    }
  }, [selectedCase]);

  return (
    <section
      id="case-studies"
      style={{
        padding: '80px 0',
        position: 'relative',
        zIndex: 2,
        background: '#060816',
      }}
    >
      <div className="ht-container">
        {/* Section Header */}
        <div className="section-header" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <span className="section-tag" style={{ background: 'rgba(0, 212, 255, 0.12)', color: '#00D4FF', padding: '6px 16px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '1px', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
            ENTERPRISE CASE STUDIES & PORTFOLIO
          </span>
          <h2 className="text-gradient" style={{ fontSize: 'clamp(2.2rem, 3.8vw, 3.2rem)', fontWeight: 800, margin: '16px 0 12px 0', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Real Impact Built for Industry Leaders
          </h2>
          <p style={{ color: '#A3A8B8', fontSize: '1.12rem', maxWidth: '780px', margin: '0 auto', lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
            Explore how Hungry Top Technologies delivers high-concurrency, SEO-optimized, and security-certified enterprise software engineering.
          </p>
        </div>

        {/* Filter and Search Bar */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            marginBottom: '40px',
            background: 'rgba(15, 23, 42, 0.6)',
            padding: '20px',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Search Bar Input */}
          <div style={{ position: 'relative', width: '100%' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#00D4FF', fontSize: '0.95rem' }}></i>
            <input
              type="text"
              placeholder="Search case studies by technology (e.g. React, C++, PyTorch, Kafka), industry, or security specs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 46px',
                background: 'rgba(6, 8, 22, 0.8)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '12px',
                color: '#FFF',
                fontSize: '0.92rem',
                fontFamily: "'Inter', sans-serif",
                outline: 'none',
                transition: 'border-color 0.25s ease',
              }}
              onFocus={(e) => (e.target.style.borderColor = 'rgba(0, 212, 255, 0.8)')}
              onBlur={(e) => (e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)')}
            />
          </div>

          {/* Category Tabs Scrollable Row */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              paddingBottom: '6px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {categories.map((cat) => {
              const active = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '30px',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    border: active ? '1px solid rgba(0, 212, 255, 0.8)' : '1px solid rgba(255, 255, 255, 0.08)',
                    background: active ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.25) 0%, rgba(108, 99, 255, 0.25) 100%)' : 'rgba(255, 255, 255, 0.03)',
                    color: active ? '#00D4FF' : '#94A3B8',
                    transition: 'all 0.25s ease',
                    boxShadow: active ? '0 4px 15px rgba(0, 212, 255, 0.2)' : 'none',
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results Counter */}
        <div style={{ marginBottom: '20px', color: '#64748B', fontSize: '0.85rem', fontWeight: 600 }}>
          Showing {filteredCases.length} Enterprise Case {filteredCases.length === 1 ? 'Study' : 'Studies'}
        </div>

        {/* Case Studies Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '24px',
          }}
          className="case-studies-grid-8"
        >
          {filteredCases.map((item) => (
            <article
              key={item.id}
              className="glass-card"
              style={{
                borderRadius: '22px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)';
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 30px rgba(0, 212, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Image Header with Category Badge Overlay */}
              <div style={{ position: 'relative', height: '190px', overflow: 'hidden', backgroundColor: '#0F172A' }}>
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.5s ease',
                  }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(6, 8, 22, 0.95) 100%)' }}></div>

                {/* Category Badge */}
                <span
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'rgba(6, 8, 22, 0.9)',
                    backdropFilter: 'blur(12px)',
                    color: '#00D4FF',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    padding: '4px 10px',
                    borderRadius: '20px',
                    border: '1px solid rgba(0, 212, 255, 0.4)',
                  }}
                >
                  {item.category}
                </span>

                {/* Main Security Badge Pill */}
                <span
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    backdropFilter: 'blur(12px)',
                    color: '#10B981',
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    padding: '4px 8px',
                    borderRadius: '16px',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <i className="fas fa-shield-alt" style={{ fontSize: '0.6rem' }}></i>
                  {item.security[0].badge}
                </span>
              </div>

              {/* Card Content */}
              <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <h3
                    style={{
                      fontSize: '1.12rem',
                      color: '#FFFFFF',
                      marginBottom: '6px',
                      fontWeight: 800,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '0.78rem', color: '#00D4FF', fontWeight: 600, marginBottom: '10px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {item.subtitle}
                  </p>
                  <p style={{ fontSize: '0.84rem', color: '#A3A8B8', lineHeight: 1.5, marginBottom: '16px', fontFamily: "'Inter', sans-serif" }}>
                    {item.description}
                  </p>

                  {/* Key Metrics Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '8px',
                      padding: '10px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      marginBottom: '14px',
                    }}
                  >
                    {item.metrics.slice(0, 2).map((m, idx) => (
                      <div key={idx} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '1rem', fontWeight: 800, color: '#00D4FF', display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          {m.value}
                        </span>
                        <span style={{ fontSize: '0.62rem', color: '#94A3B8', display: 'block', fontWeight: 600 }}>{m.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Security Compliance Pills */}
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    {item.security.slice(0, 3).map((sec, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '0.64rem',
                          background: 'rgba(255, 255, 255, 0.04)',
                          border: `1px solid ${sec.color}55`,
                          color: sec.color,
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 700,
                        }}
                      >
                        ✓ {sec.badge}
                      </span>
                    ))}
                  </div>

                  {/* Tech Stack Pills */}
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '18px' }}>
                    {item.stack.slice(0, 4).map((tech, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '0.66rem',
                          background: 'rgba(108, 99, 255, 0.15)',
                          border: '1px solid rgba(108, 99, 255, 0.3)',
                          color: '#A78BFA',
                          padding: '2px 7px',
                          borderRadius: '6px',
                          fontWeight: 600,
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* View Case Study Button */}
                <button
                  onClick={() => {
                    setSelectedCase(item);
                    setActiveTab('overview');
                  }}
                  style={{
                    width: '100%',
                    background: 'rgba(0, 212, 255, 0.08)',
                    border: '1px solid rgba(0, 212, 255, 0.25)',
                    color: '#00D4FF',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.25s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 212, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.25)';
                  }}
                >
                  <span>Read Full Case Study</span>
                  <i className="fas fa-arrow-right" style={{ fontSize: '0.72rem' }}></i>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Case Study Full Deep-Dive Modal */}
      {selectedCase && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            background: 'rgba(6, 8, 22, 0.94)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedCase(null);
          }}
        >
          <div
            className="glass-card"
            style={{
              maxWidth: '850px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '36px',
              borderRadius: '28px',
              position: 'relative',
              border: '1px solid rgba(0, 212, 255, 0.4)',
              background: 'rgba(15, 23, 42, 0.96)',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.95), 0 0 40px rgba(0, 212, 255, 0.2)',
            }}
          >
            {/* Close Modal Button */}
            <button
              onClick={() => setSelectedCase(null)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#FFF',
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
            >
              <i className="fas fa-times"></i>
            </button>

            {/* Modal Header */}
            <div>
              <span style={{ fontSize: '0.78rem', color: '#00D4FF', fontWeight: 800, textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.5px' }}>
                {selectedCase.category} • ENTERPRISE ARCHITECTURE
              </span>
              <h3 style={{ fontSize: '1.8rem', color: '#FFF', margin: '8px 0 4px 0', fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {selectedCase.title}
              </h3>
              <p style={{ color: '#94A3B8', fontSize: '0.95rem', marginBottom: '20px', fontWeight: 600 }}>
                {selectedCase.subtitle}
              </p>

              {/* All 4 Metrics Row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  marginBottom: '24px',
                }}
                className="modal-metrics-4"
              >
                {selectedCase.metrics.map((m, idx) => (
                  <div key={idx} style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#00D4FF', display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {m.value}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: '#94A3B8', display: 'block', fontWeight: 600 }}>{m.label}</span>
                  </div>
                ))}
              </div>

              {/* Navigation Tabs inside Modal */}
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  paddingBottom: '12px',
                  marginBottom: '24px',
                  overflowX: 'auto',
                }}
              >
                {[
                  { key: 'overview', label: '📋 Overview & Challenge' },
                  { key: 'architecture', label: '⚡ System Architecture' },
                  { key: 'security', label: '🛡️ Security & Compliance' },
                  { key: 'results', label: '📈 Business Impact & ROI' },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    style={{
                      background: activeTab === t.key ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
                      border: activeTab === t.key ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid transparent',
                      color: activeTab === t.key ? '#00D4FF' : '#94A3B8',
                      padding: '8px 16px',
                      borderRadius: '12px',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content Display */}
            <div style={{ color: '#E2E8F0', fontSize: '0.96rem', lineHeight: 1.65, minHeight: '220px' }}>
              {activeTab === 'overview' && (
                <div>
                  <h4 style={{ color: '#FFF', fontSize: '1.1rem', marginBottom: '10px', fontWeight: 800 }}>Executive Overview</h4>
                  <p style={{ marginBottom: '18px', color: '#CBD5E1' }}>{selectedCase.description}</p>

                  <h4 style={{ color: '#EF4444', fontSize: '1.05rem', marginBottom: '10px', fontWeight: 800 }}>The Legacy Challenge</h4>
                  <p style={{ marginBottom: '20px', color: '#CBD5E1', background: 'rgba(239, 68, 68, 0.08)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    {selectedCase.challenge}
                  </p>

                  <h4 style={{ color: '#A78BFA', fontSize: '1rem', marginBottom: '10px', fontWeight: 800 }}>Core Technologies Deployed</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedCase.stack.map((t, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '0.78rem',
                          background: 'rgba(108, 99, 255, 0.2)',
                          border: '1px solid rgba(108, 99, 255, 0.4)',
                          color: '#A78BFA',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontWeight: 700,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'architecture' && (
                <div>
                  <h4 style={{ color: '#00D4FF', fontSize: '1.1rem', marginBottom: '12px', fontWeight: 800 }}>Engineering & Traffic Scalability</h4>
                  <p style={{ marginBottom: '20px', color: '#CBD5E1', background: 'rgba(0, 212, 255, 0.05)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    {selectedCase.architecture}
                  </p>

                  {/* Architecture Highlights */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <span style={{ color: '#00D4FF', fontWeight: 700, fontSize: '0.86rem', display: 'block', marginBottom: '4px' }}>⚡ High Concurrency</span>
                      <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>Optimized for zero micro-burst packet loss & instant scaling.</span>
                    </div>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <span style={{ color: '#10B981', fontWeight: 700, fontSize: '0.86rem', display: 'block', marginBottom: '4px' }}>🔄 Fault Tolerance</span>
                      <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>Multi-region active-active deployment with automated failover.</span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div>
                  <h4 style={{ color: '#10B981', fontSize: '1.1rem', marginBottom: '12px', fontWeight: 800 }}>Security Protocol & Compliance</h4>
                  <p style={{ marginBottom: '20px', color: '#CBD5E1', background: 'rgba(16, 185, 129, 0.08)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                    {selectedCase.securityDetails}
                  </p>

                  <h4 style={{ color: '#FFF', fontSize: '0.95rem', marginBottom: '10px', fontWeight: 800 }}>Certified Security Standards</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {selectedCase.security.map((sec, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255, 255, 255, 0.03)', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${sec.color}44` }}>
                        <i className="fas fa-check-circle" style={{ color: sec.color }}></i>
                        <span style={{ color: '#FFF', fontSize: '0.84rem', fontWeight: 700 }}>{sec.badge}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'results' && (
                <div>
                  <h4 style={{ color: '#F59E0B', fontSize: '1.1rem', marginBottom: '12px', fontWeight: 800 }}>Quantified ROI & Business Value</h4>
                  <ul style={{ paddingLeft: '20px', marginBottom: '20px', color: '#CBD5E1' }}>
                    {selectedCase.roiImpact.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>
                        {item}
                      </li>
                    ))}
                  </ul>

                  {/* Client Quote Box */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, rgba(108, 99, 255, 0.15) 0%, rgba(0, 212, 255, 0.1) 100%)',
                      padding: '18px 22px',
                      borderRadius: '16px',
                      border: '1px solid rgba(108, 99, 255, 0.35)',
                    }}
                  >
                    <p style={{ fontStyle: 'italic', color: '#FFF', fontSize: '0.92rem', marginBottom: '10px' }}>
                      "{selectedCase.quote.text}"
                    </p>
                    <span style={{ fontSize: '0.78rem', color: '#00D4FF', fontWeight: 700, display: 'block' }}>
                      — {selectedCase.quote.author}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', paddingTop: '18px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', flexWrap: 'wrap', gap: '14px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: '0.72rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <i className="fas fa-lock"></i> SSL Encrypted Architecture
                </span>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setSelectedCase(null);
                    onOpenConsultation();
                  }}
                  className="btn-primary-gradient"
                  style={{
                    background: 'linear-gradient(135deg, #00D4FF 0%, #6C63FF 100%)',
                    border: 'none',
                    color: '#FFF',
                    padding: '12px 24px',
                    borderRadius: '14px',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 8px 25px rgba(0, 212, 255, 0.35)',
                  }}
                >
                  <span>Request Similar Architecture</span>
                  <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breakpoint Styles Injection */}
      <style>{`
        @media (max-width: 1280px) {
          .case-studies-grid-8 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .case-studies-grid-8 {
            grid-template-columns: 1fr !important;
          }
          .modal-metrics-4 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </section>
  );
};

export default CaseStudies;
